import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

import { ErrorBoundary } from "./components/ErrorBoundary";
import { OfflineBanner } from "./components/ui/offline-banner";

// Layouts
import { AlunoLayout } from "./components/layout/AlunoLayout";
import { EmpresaLayout } from "./components/layout/EmpresaLayout";
import { AdminLayout } from "./components/layout/AdminLayout";
import { PublicLayout } from "./components/layout/PublicLayout";
import { ProtectedRoute } from "./components/ProtectedRoute";

// Role Selection
import RoleSelection from "./pages/RoleSelection";

// Public Pages
import Oportunidades from "./pages/public/Oportunidades";
import OportunidadeDetalhe from "./pages/public/OportunidadeDetalhe";

// Aluno Pages
import AlunoLanding from "./pages/aluno/AlunoLanding";
import AlunoEscolha from "./pages/aluno/AlunoEscolha";
import AlunoCadastro from "./pages/aluno/AlunoCadastro";
import AlunoCadastroConfirmacao from "./pages/aluno/AlunoCadastroConfirmacao";
import AlunoLogin from "./pages/aluno/AlunoLogin";
import AlunoOnboarding from "./pages/aluno/AlunoOnboarding";
import AlunoHome from "./pages/aluno/AlunoHome";
import AlunoPerfil from "./pages/aluno/AlunoPerfil";
import AlunoPerfilEditar from "./pages/aluno/AlunoPerfilEditar";
import AlunoPerfilContato from "./pages/aluno/AlunoPerfilContato";
import AlunoExperiencia from "./pages/aluno/AlunoExperiencia";
import AlunoNovaExperiencia from "./pages/aluno/AlunoNovaExperiencia";
import AlunoCarreira from "./pages/aluno/AlunoCarreira";
import AlunoAprender from "./pages/aluno/AlunoAprender";
import AlunoCicloBasico from "./pages/aluno/AlunoCicloBasico";
import AlunoAplicarCurso from "./pages/aluno/AlunoAplicarCurso";
import AlunoVagas from "./pages/aluno/AlunoVagas";
import AlunoVagaDetalhe from "./pages/aluno/AlunoVagaDetalhe";
import AlunoAplicarVaga from "./pages/aluno/AlunoAplicarVaga";
import AlunoCandidaturas from "./pages/aluno/AlunoCandidaturas";
import AlunoCandidaturaDetalhe from "./pages/aluno/AlunoCandidaturaDetalhe";
import AlunoAquecimento from "./pages/aluno/AlunoAquecimento";
import AlunoHistoricoAquecimento from "./pages/aluno/AlunoHistoricoAquecimento";
import AlunoPortfolio from "./pages/aluno/AlunoPortfolio";
import AlunoPortfolioNovo from "./pages/aluno/AlunoPortfolioNovo";
import AlunoCursos from "./pages/aluno/AlunoCursos";
import AlunoCursoNovo from "./pages/aluno/AlunoCursoNovo";
import AlunoTermos from "./pages/aluno/AlunoTermos";
import AlunoVagasSalvas from "./pages/aluno/AlunoVagasSalvas";

// Empresa Pages
import EmpresaLogin from "./pages/empresa/EmpresaLogin";
import EmpresaTermos from "./pages/empresa/EmpresaTermos";
import EmpresaVitrine from "./pages/empresa/EmpresaVitrine";
import EmpresaTalentoDetalhe from "./pages/empresa/EmpresaTalentoDetalhe";
import EmpresaContatoMediado from "./pages/empresa/EmpresaContatoMediado";
import EmpresaVagas from "./pages/empresa/EmpresaVagas";
import EmpresaNovaVaga from "./pages/empresa/EmpresaNovaVaga";
import EmpresaEditarVaga from "./pages/empresa/EmpresaEditarVaga";
import EmpresaVagaDetalhe from "./pages/empresa/EmpresaVagaDetalhe";
import EmpresaVagaCandidaturas from "./pages/empresa/EmpresaVagaCandidaturas";
import EmpresaVagaCandidaturaDetalhe from "./pages/empresa/EmpresaVagaCandidaturaDetalhe";
import EmpresaShortlists from "./pages/empresa/EmpresaShortlists";
import EmpresaShortlistDetalhe from "./pages/empresa/EmpresaShortlistDetalhe";
import EmpresaRelatorios from "./pages/empresa/EmpresaRelatorios";
import EmpresaAuditoria from "./pages/empresa/EmpresaAuditoria";
import EmpresaConfiguracoes from "./pages/empresa/EmpresaConfiguracoes";
import EmpresaSolicitarParceria from "./pages/empresa/EmpresaSolicitarParceria";

// Admin Pages
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminPedidosParceria from "./pages/admin/AdminPedidosParceria";
import AdminParceiros from "./pages/admin/AdminParceiros";
import AdminVagas from "./pages/admin/AdminVagas";
import AdminPedidosContato from "./pages/admin/AdminPedidosContato";
import AdminPedidoContatoDetalhe from "./pages/admin/AdminPedidoContatoDetalhe";
import AdminAuditoria from "./pages/admin/AdminAuditoria";
import AdminRelatorios from "./pages/admin/AdminRelatorios";
import AdminVitrine from "./pages/admin/AdminVitrine";
import AdminPublicarVagas from "./pages/admin/AdminPublicarVagas";
import AdminNovaVaga from "./pages/admin/AdminNovaVaga";
import AdminEditarVaga from "./pages/admin/AdminEditarVaga";
import AdminShortlists from "./pages/admin/AdminShortlists";
import AdminShortlistDetalhe from "./pages/admin/AdminShortlistDetalhe";
import AdminConfiguracoes from "./pages/admin/AdminConfiguracoes";
import AdminTalentoDetalhe from "./pages/admin/AdminTalentoDetalhe";
import AdminParceiroDetalhe from "./pages/admin/AdminParceiroDetalhe";
import AdminMatriculas from "./pages/admin/AdminMatriculas";
import AdminCrawlerDashboard from "./pages/admin/AdminCrawlerDashboard";
import AdminCrawlerEditarVaga from "./pages/admin/AdminCrawlerEditarVaga";
import AdminVagasAtivas from "./pages/admin/AdminVagasAtivas";
import AdminVagasAtivasEditar from "./pages/admin/AdminVagasAtivasEditar";
import AdminTodasVagas from "./pages/admin/AdminTodasVagas";
import AdminTodasVagasEditar from "./pages/admin/AdminTodasVagasEditar";
import AdminRevisaoEditarVaga from "./pages/admin/AdminRevisaoEditarVaga";
import AdminEmails from "./pages/admin/AdminEmails";
import AdminEmailLog from "./pages/admin/AdminEmailLog";
import AdminEmailTemplates from "./pages/admin/AdminEmailTemplates";
import AdminEmailTemplateDetail from "./pages/admin/AdminEmailTemplateDetail";
import AdminEmailPreview from "./pages/admin/AdminEmailPreview";
import AdminEmailSend from "./pages/admin/AdminEmailSend";
import AdminEmailSendTemplate from "./pages/admin/AdminEmailSendTemplate";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

const App = () => (
  <ErrorBoundary>
  <HelmetProvider>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <OfflineBanner />
      <BrowserRouter>
        <Routes>
          {/* Role Selection */}
          <Route path="/" element={<RoleSelection />} />

          {/* Public Job Pages - Lead Generation */}
          <Route element={<PublicLayout />}>
            <Route path="/oportunidades" element={<Oportunidades />} />
            <Route path="/oportunidades/:id" element={<OportunidadeDetalhe />} />
          </Route>

          {/* Aluno Auth Flow */}
          <Route path="/aluno/landing" element={<AlunoLanding />} />
          <Route path="/aluno/escolha" element={<AlunoEscolha />} />
          <Route path="/aluno/cadastro" element={<AlunoCadastro />} />
          <Route path="/aluno/cadastro-confirmacao" element={<AlunoCadastroConfirmacao />} />
          <Route path="/aluno/login" element={<AlunoLogin />} />
          <Route path="/aluno/termos" element={<AlunoTermos />} />
          <Route path="/aluno/onboarding" element={<AlunoOnboarding />} />

          {/* Aluno App */}
          <Route path="/aluno" element={<AlunoLayout />}>
            <Route index element={<AlunoHome />} />
            <Route path="perfil" element={<AlunoPerfil />} />
            <Route path="perfil/editar" element={<AlunoPerfilEditar />} />
            <Route path="perfil/contato" element={<AlunoPerfilContato />} />
            <Route path="experiencia" element={<AlunoExperiencia />} />
            <Route path="experiencia/nova" element={<AlunoNovaExperiencia />} />
            <Route path="experiencia/portfolio" element={<AlunoPortfolio />} />
            <Route path="experiencia/portfolio/novo" element={<AlunoPortfolioNovo />} />
            <Route path="experiencia/cursos" element={<AlunoCursos />} />
            <Route path="experiencia/cursos/novo" element={<AlunoCursoNovo />} />
            <Route path="carreira" element={<AlunoCarreira />} />
            <Route path="carreira/aquecimento/:id" element={<AlunoAquecimento />} />
            <Route path="carreira/historico" element={<AlunoHistoricoAquecimento />} />
            <Route path="aprender" element={<AlunoAprender />} />
            <Route path="aprender/ciclo-basico" element={<AlunoCicloBasico />} />
            <Route path="aprender/aplicar/:cursoId" element={<AlunoAplicarCurso />} />
            <Route path="vagas" element={<AlunoVagas />} />
            <Route path="vagas/salvas" element={<AlunoVagasSalvas />} />
            <Route path="vagas/candidaturas" element={<AlunoCandidaturas />} />
            <Route path="vagas/candidaturas/:id" element={<AlunoCandidaturaDetalhe />} />
            <Route path="vagas/:id" element={<AlunoVagaDetalhe />} />
            <Route path="vagas/:id/aplicar" element={<AlunoAplicarVaga />} />
            <Route path="vagas-salvas" element={<AlunoVagasSalvas />} />
          </Route>

          {/* Empresa Auth */}
          <Route path="/empresa/login" element={<EmpresaLogin />} />
          <Route path="/empresa/termos" element={<EmpresaTermos />} />

          {/* Empresa Portal */}
          <Route path="/empresa" element={<EmpresaLayout />}>
            <Route index element={<EmpresaVitrine />} />
            <Route path="talento/:id" element={<EmpresaTalentoDetalhe />} />
            <Route path="contato-mediado" element={<EmpresaContatoMediado />} />
            <Route path="vagas" element={<EmpresaVagas />} />
            <Route path="vagas/nova" element={<EmpresaNovaVaga />} />
            <Route path="vagas/:id" element={<EmpresaVagaDetalhe />} />
            <Route path="vagas/:id/editar" element={<EmpresaEditarVaga />} />
            <Route path="vagas/:id/candidaturas" element={<EmpresaVagaCandidaturas />} />
            <Route path="vagas/:id/candidaturas/:applicationId" element={<EmpresaVagaCandidaturaDetalhe />} />
            <Route path="shortlists" element={<EmpresaShortlists />} />
            <Route path="shortlists/:id" element={<EmpresaShortlistDetalhe />} />
            <Route path="relatorios" element={<EmpresaRelatorios />} />
            <Route path="configuracoes" element={<EmpresaConfiguracoes />} />
            <Route path="auditoria" element={<EmpresaAuditoria />} />
            <Route path="solicitar-parceria" element={<EmpresaSolicitarParceria />} />
          </Route>

          {/* Admin Auth — redirects to unified empresa login */}
          <Route path="/admin/login" element={<EmpresaLogin />} />

          {/* Admin Portal — protected by admin role */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute requiredRole={["admin", "revisor"]} redirectTo="/empresa/login">
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="pedidos-parceria" element={<AdminPedidosParceria />} />
            <Route path="parceiros" element={<AdminParceiros />} />
            <Route path="parceiros/:id" element={<AdminParceiroDetalhe />} />
            <Route path="vagas" element={<AdminVagas />} />
            <Route path="vagas/:id/revisar" element={<AdminRevisaoEditarVaga />} />
            <Route path="vagas-ativas" element={<AdminVagasAtivas />} />
            <Route path="vagas-ativas/:id/editar" element={<AdminVagasAtivasEditar />} />
            <Route path="todas-vagas" element={<AdminTodasVagas />} />
            <Route path="todas-vagas/:id/editar" element={<AdminTodasVagasEditar />} />
            <Route path="pedidos-contato" element={<AdminPedidosContato />} />
            <Route path="pedidos-contato/:id" element={<AdminPedidoContatoDetalhe />} />
            <Route path="vitrine" element={<AdminVitrine />} />
            <Route path="vitrine/:id" element={<AdminTalentoDetalhe />} />
            <Route path="matriculas" element={<AdminMatriculas />} />
            <Route path="publicar-vagas" element={<AdminPublicarVagas />} />
            <Route path="publicar-vagas/nova" element={<AdminNovaVaga />} />
            <Route path="publicar-vagas/:id/editar" element={<AdminEditarVaga />} />
            <Route path="shortlists" element={<AdminShortlists />} />
            <Route path="shortlists/:id" element={<AdminShortlistDetalhe />} />
            <Route path="relatorios" element={<AdminRelatorios />} />
            <Route path="auditoria" element={<AdminAuditoria />} />
            <Route path="configuracoes" element={<AdminConfiguracoes />} />
            <Route path="robo-vagas" element={<AdminCrawlerDashboard />} />
            <Route path="robo-vagas/:id/editar" element={<AdminCrawlerEditarVaga />} />
            <Route path="emails" element={<AdminEmails />} />
            <Route path="emails/log" element={<AdminEmailLog />} />
            <Route path="emails/templates" element={<AdminEmailTemplates />} />
            <Route path="emails/templates/:key" element={<AdminEmailTemplateDetail />} />
            <Route path="emails/templates/:key/preview" element={<AdminEmailPreview />} />
            <Route path="emails/send" element={<AdminEmailSend />} />
            <Route path="emails/send/:key" element={<AdminEmailSendTemplate />} />
          </Route>

          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </HelmetProvider>
  </ErrorBoundary>
);

export default App;
